import { useState, useEffect } from "react";
import { Nav, Footer, GlobalStyles, theme, useWindowWidth } from "./design-system";

const SUGGESTIONS = ["Headache", "Sore throat", "Back pain", "Rash", "Fever", "Stomach ache", "Earache", "Cough", "Sprain", "Nosebleed"];

const QUICK_LINKS = [
  { label: "Headache",    id: "mild-headaches" },
  { label: "Sore Throat", id: "mild-sore-throat" },
  { label: "Back Pain",   id: "mild-lower-back-pain" },
  { label: "Fever",       id: "mild-fever" },
  { label: "Rash",        id: "mild-insect-reactions" },
  { label: "Chest Pain",  id: "severe-chest-pain" },
];

const HOW_IT_WORKS = [
  { n: "01", title: "Describe",  body: "Type how you feel in your own words. No medical jargon needed." },
  { n: "02", title: "Converse",  body: "CareNav asks focused follow-up questions about duration and severity." },
  { n: "03", title: "Assess",    body: "Symptoms matched against a 60-condition clinical framework." },
  { n: "04", title: "Act",       body: "Clear guidance — self-care, pharmacy, GP, 111, or A&E." },
];

const PHARMACY_FIRST = [
  { icon: "🤧", label: "Sinusitis",               desc: "Blocked or runny nose lasting over 10 days" },
  { icon: "🦷", label: "Earache",                 desc: "Mild ear pain or pressure in adults" },
  { icon: "😖", label: "Sore Throat",             desc: "Mild to moderate throat pain or scratchiness" },
  { icon: "👁️", label: "Conjunctivitis",          desc: "Red, itchy or sticky eyes" },
  { icon: "💊", label: "Urinary Tract Infection", desc: "Burning sensation when urinating" },
  { icon: "🩹", label: "Impetigo",                desc: "Sores or blisters on skin" },
  { icon: "🌡️", label: "Shingles",                desc: "Painful rash — antiviral within 72 hrs" },
  { icon: "💉", label: "Infected insect bite",    desc: "Redness, warmth, or pus at bite site" },
];

const HEALTH_ESSENTIALS = [
  { icon: "🧠", title: "Mental Health",      desc: "Access NHS talking therapies without a GP referral.", link: "https://www.nhs.uk/mental-health/", tag: "NHS resource" },
  { icon: "💊", title: "Prescriptions",      desc: "Order repeat prescriptions online and track delivery.", link: "https://www.nhs.uk/nhs-app/", tag: "NHS App" },
  { icon: "🏃", title: "Exercise Referral",  desc: "GP-referred physical activity for long-term conditions.", link: "https://www.nhs.uk/live-well/exercise/", tag: "Live Well" },
  { icon: "🩺", title: "Find a GP",          desc: "Register with a GP surgery or find a same-day appointment.", link: "https://www.nhs.uk/service-search/find-a-gp/", tag: "NHS service" },
  { icon: "🦷", title: "Dental Care",        desc: "Find an NHS dentist and check treatment costs.", link: "https://www.nhs.uk/nhs-services/dentists/", tag: "NHS service" },
  { icon: "🔬", title: "Health Checks",      desc: "Free NHS Health Check for adults aged 40–74.", link: "https://www.nhs.uk/conditions/nhs-health-check/", tag: "Prevention" },
];

function getStatusConfig(s) {
  return ({ quiet: { bg: "#DFF0E4", text: "#2A5C38", dot: "#4A7C5A" }, moderate: { bg: "#F5EAD8", text: "#7A4A1A", dot: "#C4884A" }, busy: { bg: "#F2E4D8", text: "#7A3A1A", dot: "#C4714A" }, "very busy": { bg: "#F5E0DC", text: "#7A2A1E", dot: "#B84A3A" } }[s] || { bg: theme.surfaceAlt, text: theme.textMid, dot: theme.textLight });
}

export default function HomePage() {
  const w = useWindowWidth();
  const isMobile = w < 768;
  const isTablet = w < 1024;

  const [query, setQuery]         = useState("");
  const [sugg, setSugg]           = useState([]);
  const [focused, setFocused]     = useState(false);
  const [aeData, setAeData]       = useState([]);
  const [aeLoading, setAeLoading] = useState(false);
  const [isMock, setIsMock]       = useState(false);
  const [locErr, setLocErr]       = useState(null);
  const [locAsked, setLocAsked]   = useState(false);

  useEffect(() => {
    setSugg(query.length > 1 ? SUGGESTIONS.filter(s => s.toLowerCase().includes(query.toLowerCase())).slice(0, 5) : []);
  }, [query]);

  const fetchAE = () => {
    setAeLoading(true); setLocAsked(true); setLocErr(null);
    if (!navigator.geolocation) { setLocErr("Geolocation not supported."); setAeLoading(false); return; }
    navigator.geolocation.getCurrentPosition(
      async ({ coords: { latitude: lat, longitude: lng } }) => {
        try {
          const r = await fetch(`https://co-6008-ai-chatbot-5yr6.vercel.app/api/ae/nearby?lat=${lat}&lng=${lng}&radius=40`);
          const d = await r.json();
          setAeData(d.hospitals || []); setIsMock(d.isMockData || false);
        } catch { setLocErr("Could not fetch A&E data."); }
        setAeLoading(false);
      },
      () => { setLocErr("Location access denied."); setAeLoading(false); },
      { timeout: 10000 }
    );
  };

  const px = isMobile ? "1.25rem" : "2rem";

  return (
    <div style={{ fontFamily: theme.fontBody, background: theme.bg, minHeight: "100vh" }}>
      <GlobalStyles />
      <Nav active="home" />

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <section style={{ 
        padding: isMobile ? "4rem 1.25rem 3.5rem" : "5rem 2rem 4rem", 
        maxWidth: "1100px", 
        margin: "0 auto",
        minHeight: isMobile ? "70vh" : "auto", // Balanced height, not full screen
        display: "flex",
        alignItems: "center"
      }}>
        <div style={{ 
            display: "grid", 
            gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr" : "1.1fr 0.9fr", 
            gap: isMobile ? "2.5rem" : "4rem", 
            alignItems: "center",
            width: "100%"
        }}>

          {/* Left Content */}
          <div style={{ 
              animation: "fadeUp 0.55s ease both",
              textAlign: isMobile ? "center" : "left",
              display: "flex",
              flexDirection: "column",
              alignItems: isMobile ? "center" : "flex-start"
          }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "0.45rem", background: theme.clayLight, color: theme.clay, padding: "0.26rem 0.8rem", borderRadius: theme.radiusPill, fontSize: "0.68rem", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "1.25rem" }}>
              <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: theme.clay, display: "inline-block", animation: "pulse 2s infinite" }} />
              AI triage assistant
            </div>

            <h1 style={{ 
                fontFamily: theme.fontDisplay, 
                fontSize: isMobile ? "3.2rem" : "clamp(2.4rem, 4.5vw, 3.8rem)", 
                fontWeight: "600", 
                color: theme.text, 
                lineHeight: 0.95, 
                margin: "0 0 1.5rem",
                letterSpacing: "-0.02em"
            }}>
              The right care,<br />
              <em style={{ fontStyle: "italic", color: theme.clay, fontWeight: "400" }}>right now.</em>
            </h1>

            <p style={{ 
                color: theme.textMid, 
                fontSize: isMobile ? "1.1rem" : "1.02rem", 
                lineHeight: 1.6, 
                margin: "0 0 2.5rem", 
                maxWidth: isMobile ? "300px" : "440px" 
            }}>
              Describe your symptoms in plain language. We'll find you the best care option.
            </p>

            {/* CTA Search Bar */}
            <div style={{ position: "relative", marginBottom: "1rem", width: "100%", maxWidth: "540px" }}>
              <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  background: theme.surface, 
                  border: `2px solid ${focused ? theme.clay : theme.border}`, 
                  borderRadius: theme.radiusPill, 
                  padding: isMobile ? "0.8rem 0.8rem 0.8rem 1.5rem" : "0.42rem 0.42rem 0.42rem 1.1rem", 
                  boxShadow: focused ? `0 12px 24px -8px ${theme.clayLight}` : theme.shadow, 
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)" 
              }}>
                <span style={{ color: theme.textLight, fontSize: "1.1rem", marginRight: "0.75rem" }}>🔍</span>
                <input
                  type="text" value={query} onChange={e => setQuery(e.target.value)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => { setFocused(false); setTimeout(() => setSugg([]), 150); }}
                  onKeyDown={e => e.key === "Enter" && query && (window.location.href = `/conditions/${query.toLowerCase().replace(/ /g, "-")}`)}
                  placeholder="Tell us your symptoms..."
                  style={{ flex: 1, border: "none", outline: "none", fontSize: "1.05rem", fontFamily: theme.fontBody, color: theme.text, background: "transparent", minWidth: 0 }}
                />
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent("openChatWidget"))}
                  style={{ background: theme.clay, color: "#fff", border: "none", borderRadius: theme.radiusPill, padding: isMobile ? "0.8rem 1.6rem" : "0.55rem 1.15rem", fontFamily: theme.fontBody, fontWeight: "700", fontSize: isMobile ? "0.95rem" : "0.85rem", cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.2s", flexShrink: 0 }}
                >
                  {isMobile ? "Check" : "Start check"}
                </button>
              </div>

              {sugg.length > 0 && (
                <div style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0, background: theme.surface, borderRadius: "20px", border: `1px solid ${theme.border}`, boxShadow: theme.shadowMd, overflow: "hidden", zIndex: 50, textAlign: "left" }}>
                  {sugg.map(s => (
                    <div key={s} onMouseDown={() => { setQuery(s); setSugg([]); window.location.href = `/conditions/${s.toLowerCase().replace(/ /g, "-")}`; }}
                      style={{ padding: "0.9rem 1.5rem", cursor: "pointer", fontSize: "0.95rem", color: theme.text, borderBottom: `1px solid ${theme.borderLight}`, transition: "background 0.1s" }}
                      onMouseEnter={e => e.currentTarget.style.background = theme.surfaceAlt}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >{s}</div>
                  ))}
                </div>
              )}
            </div>

            <p style={{ color: theme.textLight, fontSize: "0.75rem" }}>
              For emergencies call <strong style={{ color: theme.red }}>999</strong>
            </p>
          </div>

          {/* Hero visual — hidden on tablet and mobile */}
          {!isTablet && (
            <div style={{ display: "flex", justifyContent: "center", animation: "fadeUp 0.55s 0.12s ease both", animationFillMode: "both" }}>
              <div style={{ position: "relative", width: "300px", height: "320px" }}>
                <div style={{ position: "absolute", top: "24px", left: "16px", right: "-16px", height: "280px", background: theme.clayLight, borderRadius: theme.radiusLg, border: `1px solid ${theme.border}` }} />
                <div style={{ position: "absolute", top: 12, left: 8, right: -8, height: "280px", background: theme.bgDeep, borderRadius: theme.radiusLg, border: `1px solid ${theme.border}` }} />
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "280px", background: theme.surface, borderRadius: theme.radiusLg, border: `1px solid ${theme.border}`, boxShadow: theme.shadowLg, padding: "1.6rem", display: "flex", flexDirection: "column", gap: "0.7rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
                    <div style={{ width: "26px", height: "26px", borderRadius: "6px", background: theme.clay, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ color: "#fff", fontSize: "0.62rem", fontWeight: "700" }}>CN</span>
                    </div>
                    <span style={{ fontFamily: theme.fontDisplay, fontSize: "0.88rem", color: theme.text, fontWeight: "500" }}>CareNav</span>
                  </div>
                  {[{ label: "Symptom assessed", val: "Mild Migraine", color: theme.clay }, { label: "Recommended care", val: "Self-care", color: theme.green }, { label: "Conditions covered", val: "60+", color: theme.navy }].map(({ label, val, color }) => (
                    <div key={label} style={{ background: theme.bg, borderRadius: theme.radiusSm, padding: "0.6rem 0.8rem", border: `1px solid ${theme.border}` }}>
                      <p style={{ color: theme.textLight, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 0.18rem" }}>{label}</p>
                      <p style={{ color, fontSize: "0.88rem", fontWeight: "700", margin: 0 }}>{val}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── QUICK LINKS ─────────────────────────────────────────────────────── */}
      <div style={{ background: theme.surface, borderTop: `1px solid ${theme.border}`, borderBottom: `1px solid ${theme.border}`, padding: `1rem ${px}`, overflowX: "auto" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", display: "flex", alignItems: "center", gap: "0.55rem", flexWrap: isMobile ? "nowrap" : "wrap", minWidth: isMobile ? "max-content" : "auto" }}>
          <span style={{ color: theme.textLight, fontSize: "0.65rem", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.1em", marginRight: "0.25rem", flexShrink: 0 }}>Common</span>
          {QUICK_LINKS.map(({ label, id }) => (
            <a key={label} href={`/conditions/${id}`} style={{ background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: theme.radiusPill, padding: "0.32rem 0.8rem", fontSize: "0.78rem", color: theme.textMid, textDecoration: "none", fontWeight: "500", transition: "all 0.15s", whiteSpace: "nowrap", flexShrink: 0 }}
              onMouseEnter={e => { e.currentTarget.style.background = theme.clayLight; e.currentTarget.style.color = theme.clay; e.currentTarget.style.borderColor = theme.clayMid; }}
              onMouseLeave={e => { e.currentTarget.style.background = theme.bg; e.currentTarget.style.color = theme.textMid; e.currentTarget.style.borderColor = theme.border; }}
            >{label}</a>
          ))}
          {!isMobile && <a href="/conditions" style={{ marginLeft: "auto", color: theme.clay, fontSize: "0.78rem", textDecoration: "none", fontWeight: "600", flexShrink: 0 }}>View all →</a>}
        </div>
      </div>

      {/* ── HOW IT WORKS ────────────────────────────────────────────────────── */}
      <section style={{ padding: isMobile ? "3rem 1.25rem" : "5rem 2rem", maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ marginBottom: isMobile ? "2rem" : "2.75rem" }}>
          <p style={{ color: theme.clay, fontSize: "0.68rem", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.12em", margin: "0 0 0.6rem" }}>Process</p>
          <h2 style={{ fontFamily: theme.fontDisplay, fontSize: isMobile ? "1.6rem" : "clamp(1.7rem, 3vw, 2.3rem)", fontWeight: "500", color: theme.text, margin: 0 }}>How CareNav works</h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: "0.9rem" }}>
          {HOW_IT_WORKS.map(({ n, title, body }, i) => (
            <div key={n} style={{ padding: isMobile ? "1.1rem" : "1.5rem", borderRadius: theme.radius, border: `1px solid ${theme.border}`, background: theme.surface, boxShadow: theme.shadow, animation: `fadeUp 0.5s ${i * 0.08}s ease both` }}>
              <p style={{ fontFamily: theme.fontDisplay, fontSize: isMobile ? "1.8rem" : "2.2rem", fontWeight: "400", color: theme.border, margin: "0 0 0.75rem", lineHeight: 1 }}>{n}</p>
              <h3 style={{ fontWeight: "700", fontSize: "0.88rem", color: theme.text, margin: "0 0 0.35rem" }}>{title}</h3>
              <p style={{ color: theme.textMid, fontSize: "0.8rem", lineHeight: 1.55, margin: 0 }}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── PHARMACY FIRST ──────────────────────────────────────────────────── */}
      <section style={{ background: theme.bgDeep, borderTop: `1px solid ${theme.border}`, borderBottom: `1px solid ${theme.border}`, padding: isMobile ? "3rem 1.25rem" : "5rem 2rem" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr", gap: isMobile ? "2rem" : "4rem", alignItems: "start" }}>

            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", background: theme.clayLight, color: theme.clay, padding: "0.24rem 0.72rem", borderRadius: theme.radiusPill, fontSize: "0.66rem", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "1rem" }}>
                💊 Pharmacy First
              </div>
              <h2 style={{ fontFamily: theme.fontDisplay, fontSize: isMobile ? "1.5rem" : "clamp(1.5rem, 2.5vw, 2rem)", fontWeight: "500", color: theme.text, margin: "0 0 0.9rem", lineHeight: 1.25 }}>
                Skip the GP.<br />See your pharmacist.
              </h2>
              <p style={{ color: theme.textMid, fontSize: "0.86rem", lineHeight: 1.7, margin: "0 0 1.25rem" }}>
                For these eight conditions, your local pharmacist can supply prescription-strength treatment — no GP appointment needed.
              </p>
              <a href="https://www.nhs.uk/nhs-services/pharmacies/pharmacy-first/" target="_blank" rel="noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", background: theme.clay, color: "#fff", textDecoration: "none", padding: "0.58rem 1.1rem", borderRadius: theme.radiusPill, fontSize: "0.8rem", fontWeight: "600", transition: "all 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.background = theme.clayDark}
                onMouseLeave={e => e.currentTarget.style.background = theme.clay}
              >Find a pharmacy →</a>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0.75rem" }}>
              {PHARMACY_FIRST.map(({ icon, label, desc }) => (
                <div key={label} style={{ background: theme.bg, borderRadius: theme.radius, padding: "1rem 1.1rem", border: `1px solid ${theme.border}`, transition: "all 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = theme.surface; e.currentTarget.style.borderColor = theme.clayMid; }}
                  onMouseLeave={e => { e.currentTarget.style.background = theme.bg; e.currentTarget.style.borderColor = theme.border; }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", marginBottom: "0.3rem" }}>
                    <span style={{ fontSize: "1.05rem" }}>{icon}</span>
                    <p style={{ fontWeight: "700", fontSize: "0.84rem", color: theme.text, margin: 0 }}>{label}</p>
                  </div>
                  <p style={{ color: theme.textMid, fontSize: "0.76rem", lineHeight: 1.55, margin: 0 }}>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── HEALTH ESSENTIALS ───────────────────────────────────────────────── */}
      <section style={{ padding: isMobile ? "3rem 1.25rem" : "5rem 2rem", maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ marginBottom: isMobile ? "2rem" : "2.75rem" }}>
          <p style={{ color: theme.clay, fontSize: "0.68rem", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.12em", margin: "0 0 0.6rem" }}>Resources</p>
          <h2 style={{ fontFamily: theme.fontDisplay, fontSize: isMobile ? "1.6rem" : "clamp(1.7rem, 3vw, 2.3rem)", fontWeight: "500", color: theme.text, margin: "0 0 0.5rem" }}>Health essentials</h2>
          <p style={{ color: theme.textMid, fontSize: "0.86rem", lineHeight: 1.7, margin: 0, maxWidth: "440px" }}>High-traffic NHS services surfaced so you do not have to search for them.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(290px, 1fr))", gap: "0.9rem" }}>
          {HEALTH_ESSENTIALS.map(({ icon, title, desc, link, tag }) => (
            <a key={title} href={link} target="_blank" rel="noreferrer"
              style={{ background: theme.bg, borderRadius: theme.radius, padding: "1.25rem 1.35rem", border: `1px solid ${theme.border}`, textDecoration: "none", display: "flex", gap: "0.9rem", alignItems: "flex-start", transition: "all 0.15s", boxShadow: theme.shadow }}
              onMouseEnter={e => { e.currentTarget.style.background = theme.surface; e.currentTarget.style.borderColor = theme.clay; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = theme.shadowMd; }}
              onMouseLeave={e => { e.currentTarget.style.background = theme.bg; e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = theme.shadow; }}
            >
              <div style={{ width: "38px", height: "38px", borderRadius: "9px", background: theme.clayLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", flexShrink: 0 }}>{icon}</div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", marginBottom: "0.25rem", flexWrap: "wrap" }}>
                  <p style={{ fontWeight: "700", fontSize: "0.87rem", color: theme.text, margin: 0 }}>{title}</p>
                  <span style={{ background: theme.slateLight, color: theme.slate, fontSize: "0.6rem", fontWeight: "600", padding: "0.12rem 0.45rem", borderRadius: theme.radiusPill, textTransform: "uppercase", letterSpacing: "0.06em" }}>{tag}</span>
                </div>
                <p style={{ color: theme.textMid, fontSize: "0.79rem", lineHeight: 1.55, margin: 0 }}>{desc}</p>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* ── A&E ─────────────────────────────────────────────────────────────── */}
      <section style={{ background: theme.bgDark, padding: isMobile ? "3rem 1.25rem" : "4.5rem 2rem" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem", marginBottom: "1.75rem" }}>
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "0.38rem", marginBottom: "0.6rem" }}>
                <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#4A7C5A", animation: "pulse 2s infinite" }} />
                <span style={{ color: "rgba(245,240,232,0.32)", fontSize: "0.65rem", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.1em" }}>Live</span>
              </div>
              <h2 style={{ fontFamily: theme.fontDisplay, fontSize: isMobile ? "1.5rem" : "clamp(1.5rem, 2.5vw, 1.9rem)", fontWeight: "500", color: "#F5F0E8", margin: 0 }}>A&E Waiting Times</h2>
            </div>
            {!locAsked ? (
              <button onClick={fetchAE} style={{ background: theme.clay, color: "#fff", border: "none", padding: "0.58rem 1.1rem", borderRadius: theme.radiusPill, fontFamily: theme.fontBody, fontWeight: "600", fontSize: "0.83rem", cursor: "pointer", transition: "all 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.background = theme.clayDark}
                onMouseLeave={e => e.currentTarget.style.background = theme.clay}
              >Show hospitals near me</button>
            ) : !aeLoading && (
              <button onClick={fetchAE} style={{ background: "transparent", color: "rgba(245,240,232,0.32)", border: "1px solid rgba(245,240,232,0.12)", padding: "0.45rem 0.9rem", borderRadius: theme.radiusPill, fontFamily: theme.fontBody, fontSize: "0.76rem", cursor: "pointer" }}>Refresh</button>
            )}
          </div>

          {aeLoading && <p style={{ color: "rgba(245,240,232,0.38)", fontSize: "0.86rem" }}>Finding hospitals near you...</p>}
          {locErr && <p style={{ color: "#F5E0DC", fontSize: "0.83rem" }}>{locErr}</p>}

          {!locAsked && !aeLoading && (
            <div style={{ border: "1px dashed rgba(245,240,232,0.1)", borderRadius: theme.radius, padding: isMobile ? "2rem 1.25rem" : "2.5rem", textAlign: "center" }}>
              <p style={{ color: "rgba(245,240,232,0.26)", fontSize: "0.86rem", margin: "0 0 1rem" }}>Allow location access to see nearby A&E wait times</p>
              <button onClick={fetchAE} style={{ background: theme.clay, color: "#fff", border: "none", padding: "0.62rem 1.25rem", borderRadius: theme.radiusPill, fontFamily: theme.fontBody, fontWeight: "600", fontSize: "0.85rem", cursor: "pointer" }}>Show hospitals near me</button>
            </div>
          )}

          {aeData.length > 0 && !aeLoading && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(255px, 1fr))", gap: "0.85rem" }}>
                {aeData.map(h => {
                  const c = getStatusConfig(h.status);
                  return (
                    <div key={h.id} style={{ background: "rgba(245,240,232,0.04)", borderRadius: theme.radius, padding: "1.1rem 1.3rem", border: "1px solid rgba(245,240,232,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <p style={{ color: "#F5F0E8", fontWeight: "600", fontSize: "0.83rem", margin: "0 0 0.18rem" }}>{h.name}</p>
                        <p style={{ color: "rgba(245,240,232,0.26)", fontSize: "0.68rem", margin: "0 0 0.4rem" }}>{h.distanceKm} km away</p>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: "0.28rem", background: c.bg, color: c.text, padding: "0.15rem 0.48rem", borderRadius: theme.radiusPill, fontSize: "0.63rem", fontWeight: "700", textTransform: "uppercase" }}>
                          <div style={{ width: "4px", height: "4px", borderRadius: "50%", background: c.dot }} />{h.status}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <p style={{ color: "#F5F0E8", fontSize: "1.6rem", fontFamily: theme.fontDisplay, fontWeight: "400", margin: 0, lineHeight: 1 }}>{h.wait ?? "—"}</p>
                        <p style={{ color: "rgba(245,240,232,0.22)", fontSize: "0.6rem", margin: 0 }}>min wait</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {isMock && <p style={{ color: "rgba(245,240,232,0.15)", fontSize: "0.67rem", marginTop: "0.85rem" }}>Estimated times shown. Live data temporarily unavailable.</p>}
            </>
          )}
          <p style={{ color: "rgba(245,240,232,0.15)", fontSize: "0.67rem", marginTop: "1.5rem" }}>Life-threatening emergency? Call <strong style={{ color: "#F5E0DC" }}>999</strong> immediately.</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}